import { HRChoicePage, useConfigurations } from '@openeducation/hr-rn-components';
import { pendingPurchaserSwitchAtom } from '@openeducation/hr-rn-jotai-atoms';
import { useConfig } from '@openeducation/hr-rn-providers';
import { OrganizationAccess, OrganizationType } from '@openeducation/hr-rn-shared-types';
import { useAtom } from 'jotai';
import React, { useEffect } from 'react';

import { useNavigate, OEPath, OMPath } from '../router';

const ChoiceScreen = () => {
  const navigate = useNavigate();

  const { organizations, configurationsLoading } = useConfigurations();
  const { config } = useConfig();
  const [pendingSwitch, setPendingSwitch] = useAtom(pendingPurchaserSwitchAtom);

  const onProductSelect = (organization: OrganizationType) => {
    switch (organization) {
      case OrganizationType.OPEN_MUNDO: {
        navigate(OMPath.Home);
        break;
      }
      case OrganizationType.OPEN_ENGLISH:
      default:
        navigate(OEPath.Home);
    }
  };

  // Auto-skip the picker when arriving via the purchaser switcher (only).
  // Two rules: (1) the new purchaser has a single allowed org → pick it;
  // (2) it has multiple but includes the org the user was just on → pick that.
  // Otherwise clear the signal and let the user choose manually.
  //
  // Wait for organizations to be non-empty before consuming the signal:
  // useConfigurations exposes orgs via userOrganizationsAtom, which is
  // populated by an effect one render *after* configurationsLoading flips
  // false. Consuming earlier nukes the signal before the rules can evaluate.
  useEffect(() => {
    if (!pendingSwitch || configurationsLoading) {
      return;
    }
    if (!organizations || organizations.length === 0) {
      return;
    }
    const allowed = organizations.filter((o) => o.state === OrganizationAccess.ALLOW);
    let target: OrganizationType | null = null;
    if (allowed.length === 1) {
      target = allowed[0].type;
    } else if (
      pendingSwitch.fromOrganization &&
      allowed.some((o) => o.type === pendingSwitch.fromOrganization)
    ) {
      target = pendingSwitch.fromOrganization;
    }
    setPendingSwitch(null);
    if (target) {
      onProductSelect(target);
    }
    // onProductSelect is stable enough for this effect — only navigate-bound.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSwitch, configurationsLoading, organizations, setPendingSwitch]);

  if (configurationsLoading || !config?.environment) {
    return null;
  }

  return <HRChoicePage organizations={organizations || []} onProductSelect={onProductSelect} />;
};

export default ChoiceScreen;
